import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { services } from "../../mocks/siteData";

const servicesFaq = [
  {
    question: "What services does an interior design company offer?",
    answer: "The Design Proposal offers five core interior design planning services: custom design proposals, renovation planning and layout design, material and finish selection, room-by-room design packages, and virtual design consultations. Each service is delivered in a structured, actionable format tailored to your space, style, and budget.",
  },
  {
    question: "What is a design proposal and what does it include?",
    answer: "A design proposal is a comprehensive, actionable document that outlines your interior design plan. It includes layout planning, furniture direction, material and finish selections, color strategy, and budget-aligned recommendations — all structured clearly so you or your contractor can execute the vision without guesswork.",
  },
  {
    question: "Do I need an interior designer for my renovation?",
    answer: "A professional design plan significantly reduces the risk of costly renovation mistakes. A structured interior design proposal clarifies decisions before work begins, aligns your choices with your budget, and gives contractors a clear direction — saving both time and money throughout the renovation process.",
  },
  {
    question: "How much does material and finish selection cost?",
    answer: "Material and finish selection pricing depends on project scope, the number of rooms involved, and the level of detail required. The investment typically saves significantly more than it costs by preventing incorrect purchases, mismatched finishes, and supplier missteps that commonly occur without professional curation and guidance.",
  },
  {
    question: "Can I get a room design package for just one room?",
    answer: "Yes. Our room-by-room design packages are designed for clients who want professional interior design guidance focused on a single space. Each package delivers the same thorough detail as a full-home proposal — furniture planning, material direction, styling guidance — focused entirely on one room.",
  },
];

export default function ServicesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>
        <section className="pt-36 pb-24 relative min-h-[540px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/39d4333694b593ec8d5db4f66d9b4385.jpg"
              alt="Interior design services"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl">
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
                Our Services
              </span>
              <h1
                className="text-5xl lg:text-6xl font-semibold text-white mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Design Services Built Around Your Space
              </h1>
              <p className="text-white/70 text-lg max-w-xl mb-10 leading-relaxed">
                Professional interior design planning services tailored to your goals, style, and budget — delivered in a clear, actionable format.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/#proposal"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white font-medium px-7 py-4 rounded-md hover:bg-[#b8924d] transition-all cursor-pointer"
                >
                  Get Your Design Proposal
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
                <a
                  href="tel:+18005550199"
                  className="whitespace-nowrap inline-flex items-center gap-2 border border-white/40 text-white font-medium px-7 py-4 rounded-md hover:bg-white/10 transition-all cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  Call (800) 555-0199
                </a>
              </div>
            </div>
          </div>
        </section>

        {services.map((service, idx) => (
          <div key={service.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}>
            <div className="max-w-7xl mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? "" : ""}`}>
                <div
                  className={`relative ${idx % 2 === 1 ? "lg:order-2" : ""}`}
                  style={{ minHeight: "480px" }}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                </div>
                <div
                  className={`p-12 lg:p-16 flex flex-col justify-center ${
                    idx % 2 === 1 ? "lg:order-1" : ""
                  }`}
                >
                  <span
                    className="text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3"
                  >
                    Service 0{idx + 1}
                  </span>
                  <h2
                    className="text-3xl lg:text-4xl font-semibold text-[#111111] mb-5 leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {service.title}
                  </h2>
                  <p className="text-[#5C5C5C] leading-relaxed mb-6 text-[15px]">
                    {service.longDescription}
                  </p>
                  <ul className="space-y-2.5 mb-8">
                    {service.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#C9A961]/15 rounded-full shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#C9A961] text-xs"></i>
                        </div>
                        <span className="text-sm text-[#111111]">{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/#proposal"
                    className="whitespace-nowrap self-start inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-[#C9A961] transition-colors duration-300 cursor-pointer"
                  >
                    Request This Service
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-[#111111] rounded-2xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: "repeating-linear-gradient(45deg, #C9A961 0, #C9A961 1px, transparent 0, transparent 50%)",
                  backgroundSize: "20px 20px",
                }}
              ></div>
              <div className="lg:col-span-8 relative z-10">
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  About Our Services
                </span>
                <h2
                  className="text-3xl lg:text-4xl font-semibold text-white mb-4 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  What Interior Design Planning Actually Delivers
                </h2>
                <p className="text-white/60 leading-relaxed mb-3 text-[15px]">
                  The Design Proposal provides professional interior design planning services for homeowners across the United States. Our services include layout planning, material and finish selection, furniture direction, and renovation guidance — each delivered in a structured proposal format.
                </p>
                <p className="text-white/60 leading-relaxed text-[15px]">
                  Every proposal is tailored to your specific space, budget, and goals. We serve clients virtually across all 50 states, with local services available in select metropolitan areas. Our approach eliminates costly mistakes, speeds up decision-making, and ensures every design choice is intentional and budget-aligned.
                </p>
              </div>
              <div className="lg:col-span-4 relative z-10 grid grid-cols-2 gap-4">
                {[
                  { num: "500+", label: "Proposals Delivered" },
                  { num: "98%", label: "Satisfaction Rate" },
                  { num: "12+", label: "Years Experience" },
                  { num: "50", label: "States Served" },
                ].map((s) => (
                  <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <div
                      className="text-2xl font-semibold text-[#C9A961]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {s.num}
                    </div>
                    <div className="text-xs text-white/50 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              <div className="lg:col-span-2">
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  Services FAQ
                </span>
                <h2
                  className="text-3xl lg:text-4xl font-semibold text-[#111111] leading-tight mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Design Service Questions Answered
                </h2>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">
                  Common questions about our interior design planning services, process, and what to expect.
                </p>
              </div>
              <div className="lg:col-span-3">
                {servicesFaq.map((item, idx) => (
                  <div key={idx} className="border-b border-[#EBEBEB]">
                    <button
                      className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span
                        className={`text-sm font-medium leading-snug pr-4 transition-colors ${
                          openFaq === idx ? "text-[#C9A961]" : "text-[#111111] group-hover:text-[#C9A961]"
                        }`}
                      >
                        {item.question}
                      </span>
                      <div
                        className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all ${
                          openFaq === idx ? "bg-[#C9A961]" : "border border-[#E8E3DC]"
                        }`}
                      >
                        <i
                          className={`${openFaq === idx ? "ri-subtract-line text-white" : "ri-add-line text-[#111111]"} text-sm`}
                        ></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === idx ? "max-h-48 pb-5" : "max-h-0"
                      }`}
                    >
                      <p className="text-[#5C5C5C] leading-relaxed text-sm">{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#111111]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
              Get Started
            </span>
            <h2
              className="text-3xl lg:text-4xl font-semibold text-white mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Not Sure Which Service Is Right for You?
            </h2>
            <p className="text-white/55 mb-8 leading-relaxed">
              Tell us about your project, space, and goals — and we&apos;ll recommend the best service for your situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#proposal"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white font-medium px-7 py-3.5 rounded-md hover:bg-[#b8924d] transition-colors cursor-pointer"
              >
                Get Your Design Proposal
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <a
                href="tel:+18005550199"
                className="whitespace-nowrap inline-flex items-center gap-2 border border-white/25 text-white font-medium px-7 py-3.5 rounded-md hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call (800) 555-0199
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
